/**
 * Push-to-talk Whisper STT — replacement for the browser Web Speech API.
 *
 * Founder ask 2026-05-26: Krishna's team rated browser STT 3/10 in the
 * field. Browser engines top out around 60-70% on Indian English +
 * cricket vocab. Whisper-large-v3-turbo on Groq does ~95% on the same
 * audio at ~₹0.06/min.
 *
 * Push-to-talk specifically (not continuous) because:
 *   - Single, defined audio window per utterance → no VAD problems
 *   - Operator always knows when the mic is hot (button held = recording)
 *   - Cuts ambient noise from the audio sent up (cheaper + more accurate)
 *   - Hold-V hotkey feels natural — same key as the BBP voice toggle
 *
 * Contract:
 *     const v = useWhisperSTT({ onTranscript: (text) => parser(text) });
 *     <button onMouseDown={v.startRecording} onMouseUp={v.stopRecording}>...</button>
 *
 * Browser support: MediaRecorder API is Chrome / Edge / Safari / Firefox.
 * Vastly broader than Web Speech API (which Safari/Firefox barely
 * support). So switching to Whisper also widens browser compatibility.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";
import {
  enqueue as enqueueOffline,
  flushAll as flushOfflineAll,
  peekCount as peekOfflineCount,
} from "../lib/voiceOfflineQueue";


// MIME type negotiation. Most browsers ship the Opus codec in WebM;
// Safari prefers MP4-AAC. We try in order of preference and Groq
// accepts all common formats so whichever the browser gives us works.
const MIME_TYPES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/ogg;codecs=opus",
  "audio/mp4",
  "audio/mpeg",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return ""; // browser default
}


export function useWhisperSTT({
  onTranscript, scope = null, matchId = null,
  // Called with the backend's utterance_id whenever a transcribe
  // returns one. BBP collects these and POSTs to /api/scoring/voice/link/
  // on ball save so each ball gets its audio attached.
  onUtterance = null,
  // 2026-06-18 — Called when the backend rejects the audio as silent
  // or hallucinated (diagnostic="no_speech_detected"). The calibration
  // gauntlet uses this to score the silence-handling pass/fail —
  // anyone else can ignore it. Receives {raw, avg_logprob,
  // no_speech_prob, audio_size_bytes} so the consumer can show why.
  onRejected = null,
}) {
  const supported = typeof navigator !== "undefined"
    && navigator.mediaDevices?.getUserMedia
    && typeof MediaRecorder !== "undefined";

  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState("");
  const [lastTranscript, setLastTranscript] = useState("");
  // Live audio level 0-1 — sampled from a separate AnalyserNode during
  // PTT recording so the UI can render a "mic is hot" indicator.
  // Updated ~30Hz. Founder ask 2026-05-26: operators don't trust the
  // recorder unless they can see the level moving.
  const [audioLevel, setAudioLevel] = useState(0);
  const levelCtxRef = useRef(null);
  const levelAnalyserRef = useRef(null);
  const levelRafRef = useRef(null);
  // T128: stadium-noise warning. We collect RMS samples during PTT
  // recording, and on stop compute the 20th-percentile floor — that
  // proxies for the ambient noise level (operator pauses + breaths +
  // background, with speech peaks excluded). If the floor exceeds
  // NOISE_THRESHOLD, the parent UI can warn the operator that STT
  // accuracy will degrade.
  const levelSamplesRef = useRef([]);   // reset on startLevelMeter
  const [noiseFloor, setNoiseFloor] = useState(0);
  // 2026-06-18 — capture the peak RMS sample of the LAST utterance so
  // the rejection toast can say "peak 4% — mic too quiet" instead of
  // a vague "didn't catch that". Updated by stopLevelMeter().
  const lastPeakRef = useRef(0);

  // T132: count of utterances waiting in the offline queue. Updated on
  // enqueue and after each flush so the panel can show "Queued: 3"
  // and the operator knows their audio is safe even if the network's
  // dropped. Initial value reflects any items still in the queue from
  // a previous session.
  const [queuedCount, setQueuedCount] = useState(() => {
    try { return peekOfflineCount(); } catch { return 0; }
  });

  // T132: auto-flush when the browser regains connectivity. The
  // sender function POSTs each queued blob to /api/scoring/ai/transcribe/
  // using the same FormData shape the live path uses. On success the
  // entry is dropped from the queue; on failure it stays for the next
  // online event or manual retry. Idle when navigator.onLine is false.
  useEffect(() => {
    const sender = async (blob, ext, matchId) => {
      const form = new FormData();
      form.append("audio", blob, `utterance.${ext}`);
      if (matchId) form.append("match_id", matchId);
      await api.post("/api/scoring/ai/transcribe/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 20000,
      });
    };
    const handler = async () => {
      try {
        const { sent, remaining } = await flushOfflineAll(sender);
        setQueuedCount(remaining);
        if (sent > 0) {
          // eslint-disable-next-line no-console
          console.info(`voice offline queue: replayed ${sent}, ${remaining} remaining`);
        }
      } catch { /* ignore — next online event will retry */ }
    };
    window.addEventListener("online", handler);
    // Also fire once on mount in case we came back online before the
    // tab regained focus and missed the event.
    if (typeof navigator !== "undefined" && navigator.onLine) handler();
    return () => window.removeEventListener("online", handler);
  }, []);

  const streamRef = useRef(null);          // processed stream (post noise-gate) handed to MediaRecorder
  const rawStreamRef = useRef(null);       // raw getUserMedia stream — kept for teardown
  const processCtxRef = useRef(null);      // AudioContext for the noise-gate chain
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const mimeRef = useRef("");

  // 2026-05-28 — browser-side noise gate.
  //
  // Update later same day: defaulted OFF after a sharp drop in Whisper
  // accuracy was traced to the preprocessing chain. Whisper's model is
  // ALREADY robust to noise — adding a high-pass + dynamics compressor
  // in front of it attenuates speech transients before Whisper sees
  // them, which costs accuracy more than the noise reduction gains.
  // The toggle stays available (operators can experiment) but every
  // device now defaults to OFF, and we actively clear any old "1"
  // value persisted from the previous default-ON build so existing
  // sessions don't keep the bad chain running.
  const NOISE_GATE_STORAGE_KEY = "zcd_voice_noise_gate";
  const [noiseGateEnabled, _setNoiseGateEnabled] = useState(() => {
    try {
      // One-time cleanup: if a previous session persisted the
      // default-ON value, drop it so the new default-OFF wins. After
      // this lands, the only path back to ON is the explicit toggle.
      const v = localStorage.getItem(NOISE_GATE_STORAGE_KEY);
      if (v === "1") localStorage.removeItem(NOISE_GATE_STORAGE_KEY);
    } catch { /* ignore */ }
    return false;  // default OFF — Whisper does better on raw audio
  });
  const setNoiseGateEnabled = useCallback((on) => {
    _setNoiseGateEnabled(on);
    try { localStorage.setItem(NOISE_GATE_STORAGE_KEY, on ? "1" : "0"); } catch { /* ignore */ }
    // Tear down the processed chain (if any) and clear the cached
    // stream pointer. The raw getUserMedia stream is preserved — next
    // ensureStream() rebuilds the processed chain on top (or returns
    // the raw stream directly when the gate is OFF).
    try { processCtxRef.current?.close(); } catch { /* ignore */ }
    processCtxRef.current = null;
    streamRef.current = null;
  }, []);
  const noiseGateEnabledRef = useRef(noiseGateEnabled);
  noiseGateEnabledRef.current = noiseGateEnabled;
  // Cache the last successfully recorded audio so the operator can
  // re-run it through Whisper via Shift+V ("repeat that") without
  // re-speaking. Useful when STT slipped on a one-off — same audio,
  // second attempt sometimes resolves to the right phrase.
  const lastAudioRef = useRef({ blob: null, ext: "webm" });
  // Hands-free state — AudioContext + AnalyserNode used by the
  // silence-detection VAD loop. When the operator opts into hands-free,
  // we keep recording but auto-stop on each ~800ms silence + restart
  // listening for the next utterance.
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadIntervalRef = useRef(null);
  const handsFreeRef = useRef(false);
  const speakingRef = useRef(false);
  const silenceStartRef = useRef(0);
  // Tracks the operator's intent — true between press-and-release of
  // V / the PTT button. If the operator releases BEFORE getUserMedia
  // (mic permission prompt) resolves, the async startRecording sees
  // wantsRecordingRef === false and aborts cleanly. Without this the
  // first PTT after a fresh page load could leave a "ghost" recorder
  // running because keyup fired before the recorder was created.
  const wantsRecordingRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const matchIdRef = useRef(matchId);
  matchIdRef.current = matchId;
  const onUtteranceRef = useRef(onUtterance);
  onUtteranceRef.current = onUtterance;
  const onRejectedRef = useRef(onRejected);
  onRejectedRef.current = onRejected;

  // Get / cache the mic stream. Browsers prompt once per origin; we
  // hold the stream across recordings to avoid the prompt firing on
  // every push.
  //
  // 2026-05-28 — when noiseGateEnabled is true (default), wrap the raw
  // mic stream in a Web Audio chain:
  //
  //   raw mic → HighPassFilter(100Hz) → DynamicsCompressor → ScriptlessGate → MediaRecorder
  //
  // - HighPassFilter at 100Hz cuts room rumble + AC hum below the
  //   speech band (men: 85Hz+, women: 165Hz+; everything below 100Hz
  //   is noise for our purposes).
  // - DynamicsCompressor with threshold=-50dB knee=10 ratio=12 acts
  //   as a soft noise gate — quiet background (PA chatter, wind,
  //   crowd hum) gets heavily attenuated while speech transients
  //   pass through. The 100ms release prevents tail-syllable
  //   chopping.
  //
  // The browser's own noiseSuppression (Chrome's WebRTC-style filter)
  // stays enabled in the constraints below — it handles steady-state
  // noise; our chain handles intermittent / impulsive noise that
  // gets through. Belt-and-braces.
  const buildNoiseGate = useCallback((rawStream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const source = ctx.createMediaStreamSource(rawStream);

      const highpass = ctx.createBiquadFilter();
      highpass.type = "highpass";
      highpass.frequency.value = 100;
      highpass.Q.value = 0.7;

      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = -50;
      compressor.knee.value = 10;
      compressor.ratio.value = 12;
      compressor.attack.value = 0.003;
      compressor.release.value = 0.1;

      const destination = ctx.createMediaStreamDestination();

      source.connect(highpass);
      highpass.connect(compressor);
      compressor.connect(destination);

      processCtxRef.current = ctx;
      return destination.stream;
    } catch (exc) {
      // Non-fatal — fall back to raw stream.
      console.warn("noise-gate setup failed, using raw stream:", exc);
      return null;
    }
  }, []);

  const ensureStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    if (!supported) throw new Error("MediaRecorder not supported in this browser");
    try {
      // Reuse the raw mic stream across noise-gate toggles so the
      // browser permission prompt only fires once per session.
      //
      // 2026-06-18 — WebRTC filters flipped to DEFAULT-OFF after a
      // gauntlet showed every utterance returning `.` / hallucinations.
      // Hard evidence (avg_logprob -0.5 to -3.2 on user audio + Whisper
      // collapsing to "Thank you" with no prompt) confirmed the audio
      // reaching MediaRecorder was near-silent — Chrome's
      // noiseSuppression was over-cancelling Adhish's voice on his
      // built-in laptop mic. Opt-in toggle via localStorage flag for
      // anyone in a stadium who needs them back.
      let webrtcFilters = false;
      try {
        webrtcFilters = localStorage.getItem("zcd_voice_webrtc_filters") === "1";
      } catch { /* private mode etc */ }
      let raw = rawStreamRef.current;
      if (!raw || raw.getTracks().every((t) => t.readyState !== "live")) {
        raw = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: webrtcFilters,
            noiseSuppression: webrtcFilters,
            autoGainControl: webrtcFilters,
            channelCount: 1,
            sampleRate: 16000, // Whisper resamples to 16kHz anyway
          },
        });
        rawStreamRef.current = raw;
      }
      let stream = raw;
      if (noiseGateEnabledRef.current) {
        const processed = buildNoiseGate(raw);
        if (processed) stream = processed;
      }
      streamRef.current = stream;
      mimeRef.current = pickMimeType();
      return stream;
    } catch (exc) {
      if (exc.name === "NotAllowedError") {
        throw new Error("Microphone permission denied. Enable it in browser settings.");
      }
      throw exc;
    }
  }, [supported, buildNoiseGate]);

  // Start the live audio-level meter. Separate AudioContext from the
  // VAD analyser so the two can run independently (PTT uses the meter
  // but not VAD; hands-free uses VAD but also benefits from a meter).
  const startLevelMeter = useCallback((stream) => {
    try {
      if (levelCtxRef.current) return; // already running
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      levelCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      levelAnalyserRef.current = analyser;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      // T128: reset noise-sample buffer at the start of each recording.
      levelSamplesRef.current = [];
      const tick = () => {
        if (!levelAnalyserRef.current) return;
        analyser.getByteTimeDomainData(buf);
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const d = buf[i] - 128;
          sumSq += d * d;
        }
        const rms = Math.sqrt(sumSq / buf.length) / 128;  // 0-1
        // Apply a soft non-linear curve so quiet speech still moves
        // the bar visibly.
        const visual = Math.min(1, Math.sqrt(rms * 2.5));
        setAudioLevel(visual);
        // Stash the raw RMS (not the visual one) for noise-floor calc.
        // Cap at 1000 samples so a stuck recorder can't grow this
        // unboundedly. At ~30Hz that's ~33s of audio, plenty.
        if (levelSamplesRef.current.length < 1000) {
          levelSamplesRef.current.push(rms);
        }
        levelRafRef.current = requestAnimationFrame(tick);
      };
      levelRafRef.current = requestAnimationFrame(tick);
    } catch (exc) {
      // Non-fatal — recording still works without the meter.
      console.warn("audio level meter failed to start:", exc);
    }
  }, []);

  const stopLevelMeter = useCallback(() => {
    if (levelRafRef.current) {
      cancelAnimationFrame(levelRafRef.current);
      levelRafRef.current = null;
    }
    try { levelCtxRef.current?.close(); } catch { /* ignore */ }
    levelCtxRef.current = null;
    levelAnalyserRef.current = null;
    setAudioLevel(0);
    // 2026-06-18 — capture peak before zeroing so the rejection toast
    // can show "peak X%". Used in the no_speech_detected branch.
    if (levelSamplesRef.current.length > 0) {
      let peak = 0;
      for (const s of levelSamplesRef.current) {
        if (s > peak) peak = s;
      }
      lastPeakRef.current = peak;
    } else {
      lastPeakRef.current = 0;
    }
    // T128: compute noise-floor estimate from the samples collected
    // during this recording. We take the 20th percentile of raw RMS —
    // a robust proxy for ambient noise (speech peaks above the floor,
    // operator pauses + background sit at it). Need at least 10
    // samples for a stable estimate; otherwise leave previous value.
    const samples = levelSamplesRef.current;
    if (samples.length >= 10) {
      const sorted = [...samples].sort((a, b) => a - b);
      const p20 = sorted[Math.floor(sorted.length * 0.2)];
      setNoiseFloor(p20);
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (!supported) {
      setError("Voice recording not supported in this browser.");
      return;
    }
    // Flag intent IMMEDIATELY so a fast release-while-awaiting-mic
    // is observable by the async path below.
    wantsRecordingRef.current = true;
    setError("");
    // Optimistic UI — show the red "Recording…" state right away,
    // even before the mic permission resolves. On the first PTT
    // press of a session the prompt can take 300-1000ms; without
    // this the button looks dead.
    setRecording(true);
    try {
      const stream = await ensureStream();
      // Operator released V while permission was pending → abort.
      if (!wantsRecordingRef.current) {
        setRecording(false);
        return;
      }
      const opts = mimeRef.current ? { mimeType: mimeRef.current } : {};
      const recorder = new MediaRecorder(stream, opts);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onerror = (e) => {
        setError(`Recorder error: ${e.error?.message || e.error || "unknown"}`);
      };
      recorder.start();
      recorderRef.current = recorder;
      // Kick off the visible audio-level meter alongside recording.
      startLevelMeter(stream);
      // (recording was set true above; keep it true)
    } catch (exc) {
      // Distinguish permission-denied (helpful message) from other.
      const msg = exc?.message || String(exc);
      if (/permission|notallowed/i.test(msg)) {
        setError("Mic permission needed. Click the 🎤 icon in the address bar → Allow.");
      } else {
        setError(msg);
      }
      wantsRecordingRef.current = false;
      setRecording(false);
    }
  }, [supported, ensureStream]);

  // Stop + immediately ship the audio to /api/scoring/ai/transcribe/.
  // The hook returns the transcript via onTranscript callback; it
  // doesn't decide what to do with it (parser is the caller's
  // concern).
  const stopRecording = useCallback(async () => {
    // Clear intent first so any concurrent startRecording mid-await
    // sees `!wantsRecordingRef.current` and aborts.
    wantsRecordingRef.current = false;
    const recorder = recorderRef.current;
    if (!recorder) {
      // Recording never actually started (release-before-stream).
      // Reset UI state and bail.
      setRecording(false);
      stopLevelMeter();
      return;
    }
    setRecording(false);
    stopLevelMeter();

    // Wait for the final ondataavailable + onstop sequence before
    // posting. MediaRecorder fires onstop after flushing.
    const blob = await new Promise((resolve) => {
      recorder.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mimeRef.current || "audio/webm" });
        chunksRef.current = [];
        resolve(b);
      };
      try { recorder.stop(); } catch { resolve(new Blob()); }
    });

    if (!blob || blob.size < 200) {
      // Too short to be a real utterance (< 200 bytes ≈ < 100ms of
      // opus). 2026-06-18 — was silently dropped; now surface a hint
      // so the operator knows the press registered but was too quick.
      // The badge in VoiceScoringPanel renders ptt.error.
      setError("Held too briefly — keep V pressed while you speak.");
      return;
    }

    const ext = (mimeRef.current.includes("mp4") ? "mp4" :
                 mimeRef.current.includes("ogg") ? "ogg" : "webm");
    lastAudioRef.current = { blob, ext };

    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", blob, `utterance.${ext}`);
      if (scopeRef.current) form.append("scope", scopeRef.current);
      if (matchIdRef.current) form.append("match_id", matchIdRef.current);

      const resp = await api.post("/api/scoring/ai/transcribe/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 20000,
      });
      const text = (resp.data?.transcript || "").trim();
      const utteranceId = resp.data?.utterance_id || null;
      // 2026-06-18 — backend may return `transcript: ""` with a
      // `diagnostic` field when Whisper hallucinated on silent/quiet
      // audio. Surface a clear "didn't catch that" error instead of
      // dropping into the (now empty) parser → AI-fallback → "low
      // confidence" toast cascade. Helps the operator notice mic/level
      // problems instead of silently burning ZC on garbage parses.
      if (!text && resp.data?.diagnostic === "no_speech_detected") {
        const raw = resp.data?.raw || "";
        const peak = lastPeakRef.current;
        const peakPct = Math.round(peak * 100);
        // 2026-06-18 — peak < ~5% on the RMS 0-1 scale is essentially
        // silent input. Make the cause concrete instead of asking the
        // user to debug "didn't catch that". Threshold tuned against
        // real speech samples (typical PTT recording peaks 25-60%).
        let msg;
        if (peak < 0.05) {
          msg = `Mic peak ${peakPct}% — too quiet. Check OS mic input level + speak closer.`;
        } else if (peak < 0.15) {
          msg = `Mic peak ${peakPct}% — quiet. Speak louder${raw ? ` (Whisper heard: "${raw.slice(0, 30)}")` : ""}.`;
        } else {
          msg = `Didn't catch that — peak ${peakPct}%${raw ? ` (heard: "${raw.slice(0, 40)}")` : ""}.`;
        }
        setError(msg);
        setLastTranscript("");
        if (onRejectedRef.current) {
          try {
            onRejectedRef.current({
              raw,
              peak,
              avg_logprob: resp.data?.avg_logprob ?? null,
              no_speech_prob: resp.data?.no_speech_prob ?? null,
              audio_size_bytes: resp.data?.audio_size_bytes ?? null,
            });
          } catch { /* ignore */ }
        }
        return;
      }
      setLastTranscript(text);
      if (utteranceId && onUtteranceRef.current) {
        try { onUtteranceRef.current(utteranceId); } catch { /* ignore */ }
      }
      if (text && onTranscriptRef.current) {
        try { onTranscriptRef.current(text); }
        catch (parserExc) { console.error("voice parser threw:", parserExc); }
      }
    } catch (exc) {
      // T132: network-class failure → enqueue for replay when back
      // online. Distinguished from server errors by absence of
      // exc.response (axios sets that on any HTTP response, even
      // 5xx). Without exc.response → DNS, timeout, offline, CORS,
      // browser-killed-tab — all things a retry will fix.
      const isNetworkFailure = !exc?.response;
      if (isNetworkFailure) {
        try {
          await enqueueOffline({ blob, ext, matchId: matchIdRef.current });
          setQueuedCount(peekOfflineCount());
          setError("Offline — utterance queued, will retry when online.");
        } catch (qe) {
          // Queue write failed (storage quota?) — fall back to normal error.
          const msg = exc?.message || "Transcription failed (offline)";
          setError(msg);
        }
      } else {
        // Server responded with error — surface as before.
        const data = exc?.response?.data;
        const msg = data?.error || data?.detail || exc?.message || "Transcription failed";
        setError(msg);
      }
    } finally {
      setTranscribing(false);
    }
  }, []);

  // ── Hands-free mode ────────────────────────────────────────────────
  // VAD-driven: tap to start, the hook records continuously, watches
  // the audio level via an AnalyserNode, and on each silence > 800ms
  // it auto-stops the current recording, transcribes, then immediately
  // restarts so the next utterance is captured without operator input.
  // Tap again to fully disable.
  const [handsFree, setHandsFree] = useState(false);

  const teardownVAD = useCallback(() => {
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    speakingRef.current = false;
    silenceStartRef.current = 0;
  }, []);

  const startHandsFree = useCallback(async () => {
    if (!supported) {
      setError("Voice recording not supported in this browser.");
      return;
    }
    setError("");
    try {
      const stream = await ensureStream();

      // Set up analyser for VAD. AudioContext is wrapped in a try
      // because some browsers throw on resumption after user gesture
      // policy quirks; we surface a useful error instead of silently
      // dying.
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;

      handsFreeRef.current = true;
      setHandsFree(true);

      // Start the first recording immediately.
      await startRecording();

      // VAD loop. Samples RMS amplitude every 60ms. When level drops
      // below the threshold for ≥800ms after at least 400ms of speech,
      // we treat it as end-of-utterance: stop + transcribe, then
      // restart the recording for the next utterance.
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const SILENCE_THRESHOLD = 8;          // ~RMS units on 0-255 scale
      const SILENCE_MS = 800;
      const MIN_SPEAKING_MS = 400;
      let utteranceStart = Date.now();

      vadIntervalRef.current = setInterval(async () => {
        if (!handsFreeRef.current || !analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(buf);
        // RMS — centred at 128 (silence), max 255.
        let sumSq = 0;
        for (let i = 0; i < buf.length; i++) {
          const d = buf[i] - 128;
          sumSq += d * d;
        }
        const rms = Math.sqrt(sumSq / buf.length);
        const now = Date.now();

        if (rms > SILENCE_THRESHOLD) {
          speakingRef.current = true;
          silenceStartRef.current = 0;
        } else if (speakingRef.current) {
          if (!silenceStartRef.current) {
            silenceStartRef.current = now;
          } else if (
            now - silenceStartRef.current >= SILENCE_MS
            && now - utteranceStart >= MIN_SPEAKING_MS
          ) {
            // Auto-submit current utterance and start the next.
            speakingRef.current = false;
            silenceStartRef.current = 0;
            await stopRecording();
            if (!handsFreeRef.current) return; // user disabled while submitting
            utteranceStart = Date.now();
            await startRecording();
          }
        }
      }, 60);
    } catch (exc) {
      setError(exc.message || String(exc));
      teardownVAD();
      handsFreeRef.current = false;
      setHandsFree(false);
    }
  }, [supported, ensureStream, startRecording, stopRecording, teardownVAD]);

  const stopHandsFree = useCallback(async () => {
    handsFreeRef.current = false;
    setHandsFree(false);
    teardownVAD();
    // Flush the in-flight recording (if any).
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      await stopRecording();
    }
  }, [stopRecording, teardownVAD]);

  const toggleHandsFree = useCallback(() => {
    if (handsFreeRef.current) stopHandsFree();
    else startHandsFree();
  }, [startHandsFree, stopHandsFree]);

  // Re-run the last captured audio through Whisper. Bound to Shift+V
  // on the BBP voice panel — operator suspects a one-off mis-recognition
  // and wants a second pass on the same audio without re-speaking.
  // No-op if there's nothing cached yet.
  const repeatLast = useCallback(async () => {
    const cached = lastAudioRef.current;
    if (!cached?.blob || cached.blob.size < 200) {
      setError("No previous audio to repeat — record something first.");
      return;
    }
    setError("");
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("audio", cached.blob, `utterance.${cached.ext}`);
      if (scopeRef.current) form.append("scope", scopeRef.current);
      if (matchIdRef.current) form.append("match_id", matchIdRef.current);
      const resp = await api.post("/api/scoring/ai/transcribe/", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 20000,
      });
      const text = (resp.data?.transcript || "").trim();
      const utteranceId = resp.data?.utterance_id || null;
      if (!text && resp.data?.diagnostic === "no_speech_detected") {
        const raw = resp.data?.raw || "";
        const tail = raw ? ` (heard: "${raw.slice(0, 40)}")` : "";
        setError(`Didn't catch that — check mic / speak louder${tail}`);
        setLastTranscript("");
        return;
      }
      setLastTranscript(text);
      if (utteranceId && onUtteranceRef.current) {
        try { onUtteranceRef.current(utteranceId); } catch { /* ignore */ }
      }
      if (text && onTranscriptRef.current) {
        try { onTranscriptRef.current(text); }
        catch (exc) { console.error("voice parser threw (repeat):", exc); }
      }
    } catch (exc) {
      const data = exc?.response?.data;
      setError(data?.error || data?.detail || exc?.message || "Repeat failed");
    } finally {
      setTranscribing(false);
    }
  }, []);

  // Release the mic when the component unmounts. The browser usually
  // keeps the tab's permission grant; explicit stop() ensures the
  // little red mic indicator goes away.
  useEffect(() => {
    return () => {
      try {
        handsFreeRef.current = false;
        if (vadIntervalRef.current) clearInterval(vadIntervalRef.current);
        try { audioCtxRef.current?.close(); } catch { /* ignore */ }
        try { processCtxRef.current?.close(); } catch { /* ignore */ }
        processCtxRef.current = null;
        recorderRef.current?.stop();
        // Stop tracks on both the raw and processed streams. The
        // processed stream's tracks come from MediaStreamDestination so
        // closing the AudioContext above effectively kills them, but be
        // explicit anyway.
        rawStreamRef.current?.getTracks()?.forEach((t) => t.stop());
        streamRef.current?.getTracks()?.forEach((t) => t.stop());
        rawStreamRef.current = null;
        streamRef.current = null;
        recorderRef.current = null;
      } catch { /* ignore */ }
    };
  }, []);

  // T128: derived noisy-environment flag. The 0.08 threshold was
  // picked from a few short recordings: clean office < 0.04, light
  // background chatter ~0.06, crowded indoor noise > 0.10. Stadium
  // ambient (PA + crowd) easily exceeds 0.12. Conservative threshold
  // so we don't false-warn the operator at home; can be tuned later.
  const isNoisy = noiseFloor > 0.08;

  return {
    supported,
    recording,
    transcribing,
    error,
    lastTranscript,
    audioLevel,
    noiseFloor,
    isNoisy,
    queuedCount,
    startRecording,
    stopRecording,
    handsFree,
    toggleHandsFree,
    repeatLast,
    // 2026-05-28 — noise gate (default ON). Operator can disable via
    // VoiceScoringPanel's settings if it's chopping syllables.
    noiseGateEnabled,
    setNoiseGateEnabled,
  };
}
